import useRequest from "@/lib/request/useRequest";
import { BaseField } from "@/types/field";
import { fileInputSchema } from "@/lib/form/operation";
import { ApplicationMethod } from "@/types/enums";
import { FertilizerProduct, FertilizerType } from "@/types/products";
import { object, date, number, string, boolean, InferType } from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormProvider, useForm } from "react-hook-form";
import {
    Button,
    ButtonGroup,
    ModalContent,
    ModalBody,
    ModalHeader,
    Modal,
    ModalFooter,
    Divider,
    Alert,
} from "@heroui/react";
import DateInput from "@/components/inputs/DateInput";
import AutoCompleteInput from "../inputs/AutoCompleteInput";
import NumberInputComponent from "../inputs/NumberInput";
import TextAreaInput from "@/components/inputs/TextAreaInput";
import { useI18n } from "@/lib/i18n/useI18n";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faClipboardList,
    faSquareCheck,
    faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { sortArrayByKey } from "@/lib/utils/sorting";
import { useMemo, useState } from "react";
import {
    targetRateNToProdutcRate,
    productRateToTargetRateN,
} from "@/lib/utils/operations";
import { FieldConfig } from "@/types/fieldConfig";
import ProductAutoComplete from "@/components/inputs/ProductAutoComplete";

const applicationSchema = object().shape({
    start_date: date().required().label("Start Date"),
    n_rate: number().nullable().min(0).label("N Rate"),
    product_rate: number().nullable().min(0).label("Product Rate"),
    depth: number().nullable().min(0).label("Depth"),
    fertilizer: number().nullable().label("Fertilizer"),
    is_complete: boolean().required().label("Status"),
    notes: string().nullable().label("Notes"),
    method: string()
        .required()
        .label("Application Method")
        .oneOf([
            ApplicationMethod.InjectedKnifedCoultered,
            ApplicationMethod.Dribble,
            ApplicationMethod.Broadcast,
            ApplicationMethod.Topdress,
            ApplicationMethod.YDrop,
            ApplicationMethod.BroadcastTillage,
            ApplicationMethod.TwoXTwo,
            ApplicationMethod.Furrow,
            ApplicationMethod.Fertigation,
        ]),
    properties: fileInputSchema,
});

type BulkLogApplicationFormData = InferType<typeof applicationSchema>;

function BulkLogApplication({
    fields,
    onFinish,
    showPlotsEstablishment = false,
}: {
    fields: BaseField[];
    onFinish: () => void;
    showPlotsEstablishment?: boolean;
}) {
    const { t } = useTranslation("operations");
    const { formatUnit } = useI18n();
    const { useGetRequest, useMutateRequest } = useRequest();
    const bulkLogApplicationMutation = useMutateRequest({
        url: "application_operations/bulk_upsert/",
        method: "put",
        successMessage: "Logged application operations successfully.",
        errorMessage: "Failed to log application operations.",
    });
    const form = useForm<BulkLogApplicationFormData>({
        resolver: yupResolver(applicationSchema),
        defaultValues: {
            start_date: dayjs().toDate(),
            n_rate: undefined,
            product_rate: undefined,
            fertilizer: undefined,
            is_complete: true,
            method: ApplicationMethod.Broadcast,
            notes: null,
        },
        mode: "onTouched",
    });
    const [fertilizer, isComplete, method] = form.watch([
        "fertilizer",
        "is_complete",
        "method",
    ]);
    const fertilizersQuery = useGetRequest<FertilizerProduct[]>({
        url: `fertilizers/`,
    });
    const selectedFertilizer = useMemo(() => {
        return fertilizersQuery.data?.find((fert) => fert.id == fertilizer);
    }, [fertilizer, fertilizersQuery.data]);
    const isFertigation = method === ApplicationMethod.Fertigation;
    const methodOptions = useMemo(() => {
        const types = [
            {
                id: ApplicationMethod.InjectedKnifedCoultered,
                description: "Injected/Knifed/Coultered",
            },
            { id: ApplicationMethod.Dribble, description: "Dribble" },
            { id: ApplicationMethod.Broadcast, description: "Broadcast" },
            { id: ApplicationMethod.Topdress, description: "Topdress" },
            { id: ApplicationMethod.YDrop, description: "Y-Drop" },
            {
                id: ApplicationMethod.BroadcastTillage,
                description: "Broadcast & Incorporated",
            },
            { id: ApplicationMethod.TwoXTwo, description: "2x2" },
            { id: ApplicationMethod.Furrow, description: "In-Furrow" },
            { id: ApplicationMethod.Fertigation, description: "Fertigation" },
        ];

        return sortArrayByKey(types, "description");
    }, []);

    const [plotsEstablishment, setPlotsEstablishment] = useState<boolean>(
        (fields as FieldConfig[]).every((field) => field.latest_plot_prescription_id !== null) && (showPlotsEstablishment ?? false),
    );

    const bulkLogRxAppliedMutation = useMutateRequest({
        url: "application_operations/prescriptions/",
        method: "put",
        successMessage: "Logged Rx applications successfully.",
        errorMessage: "Failed to log Rx applications.",
    });

    const saveApplicationOperations = (data: BulkLogApplicationFormData) => {
        if (plotsEstablishment) {
            const prescriptionIds = (fields as FieldConfig[])
                .map((field) => field.latest_plot_prescription_id)
                .filter((id) => id !== null);
            bulkLogRxAppliedMutation.mutate(
                {
                    data: prescriptionIds.map((id) => ({
                        prescription: id,
                        start_date: dayjs(data.start_date).format("YYYY-MM-DD"),
                        is_complete: data.is_complete,
                        method: data.method,
                        note: data.notes,
                    })),
                },
                {
                    onSuccess: () => {
                        onFinish();
                    },
                },
            );
        } else {
            const payload = fields.map((field) => ({
                field: field.id,
                start_date: dayjs(data.start_date).format("YYYY-MM-DD"),
                field_rate: { rate: data.n_rate },
                note: data.notes,
                application_products: [
                    { price: null, product: data.fertilizer },
                ],
                is_complete: data.is_complete,
                method: data.method,
                irrigation_system: isFertigation
                    ? field.irrigation_system_configuration.default_system
                    : null,
                irrigation_operation: isFertigation
                    ? {
                        field: field.id,
                        irrigation_system:
                            field.irrigation_system_configuration
                                .default_system,
                        start_date: dayjs(data.start_date).format(
                            "YYYY-MM-DD",
                        ),
                        note: data.notes,
                        field_rate: { rate: data.depth ?? 0.5 },
                    }
                    : null,
            }));
            bulkLogApplicationMutation.mutate(
                { data: payload },
                {
                    onSuccess: () => {
                        onFinish();
                    },
                },
            );
        }
    };

    return (
        <Modal isOpen size="lg" onClose={onFinish} scrollBehavior="inside">
            <ModalContent>
                <ModalHeader>Bulk Log Applications</ModalHeader>
                <ModalBody>
                    <FormProvider {...form}>
                        <form>
                            <div className="md:flex md:flex-col md:gap-2 md:items-stretch">
                                <div className="flex flex-row gap-2 items-end">
                                    <DateInput
                                        name="start_date"
                                        label={t("date")}
                                        variant="flat"
                                        isRequired
                                        size="sm"
                                        labelPlacement="outside"
                                        showMonthAndYearPickers
                                        className="flex-1"
                                    />
                                    {showPlotsEstablishment && (fields as FieldConfig[]).every((field) => field.latest_plot_prescription_id !== null) && (
                                        <Button
                                            onPress={() =>
                                                setPlotsEstablishment(
                                                    !plotsEstablishment,
                                                )
                                            }
                                            size="sm"
                                            variant={
                                                plotsEstablishment
                                                    ? "solid"
                                                    : "faded"
                                            }
                                            color="secondary"
                                            startContent={
                                                <FontAwesomeIcon
                                                    icon={
                                                        plotsEstablishment
                                                            ? faSquareCheck
                                                            : faXmark
                                                    }
                                                />
                                            }
                                        >
                                            {t("plotsEstablishment")}
                                        </Button>
                                    )}
                                </div>
                                {plotsEstablishment && (
                                    <Alert
                                        color="warning"
                                        variant="flat"
                                        className="max-h-10 text-xs items-center"
                                    >
                                        {t("plotsEstablishmentWarning")}
                                    </Alert>
                                )}
                                <ProductAutoComplete
                                    selectedProductId={fertilizer?.toString() ?? null}
                                    setSelectedProductId={(productId: string | null) => {
                                        form.setValue("fertilizer", productId ? Number(productId) : null);
                                    }}
                                />
                                <div className="md:flex md:flex-row md:gap-4 w-full md:justify-between">
                                    <NumberInputComponent
                                        name="n_rate"
                                        label={`${t("nitrogenRate")} (${formatUnit("lb_ac", "kg_ha")})`}
                                        variant="flat"
                                        size="sm"
                                        placeholder={t("nitrogenRate")}
                                        className="flex-1"
                                        min={0}
                                        step={0.1}
                                        isRequired
                                        labelPlacement="outside"
                                        isDisabled={plotsEstablishment}
                                        onValueChange={(value) => {
                                            const calculatedProductRate =
                                                targetRateNToProdutcRate(
                                                    Number(value),
                                                    selectedFertilizer,
                                                );
                                            if (calculatedProductRate) {
                                                form.setValue(
                                                    "product_rate",
                                                    Number(
                                                        calculatedProductRate.toFixed(
                                                            2,
                                                        ),
                                                    ),
                                                );
                                            }
                                        }}
                                    />
                                    <NumberInputComponent
                                        name="product_rate"
                                        label={`${t("productRate")} (${selectedFertilizer?.fertilizer_type === FertilizerType.Liquid ? formatUnit("gal_ac", "L_ha") : formatUnit("lb_ac", "kg_ha")})`}
                                        variant="flat"
                                        size="sm"
                                        placeholder={t("productRate")}
                                        className="flex-1"
                                        min={0}
                                        step={0.1}
                                        isRequired
                                        labelPlacement="outside"
                                        isDisabled={plotsEstablishment}
                                        onValueChange={(value) => {
                                            const calculatedNRate =
                                                productRateToTargetRateN(
                                                    Number(value),
                                                    selectedFertilizer,
                                                );
                                            if (calculatedNRate) {
                                                form.setValue(
                                                    "n_rate",
                                                    Number(
                                                        calculatedNRate.toFixed(
                                                            2,
                                                        ),
                                                    ),
                                                );
                                            }
                                        }}
                                    />
                                    {isFertigation && (
                                        <NumberInputComponent
                                            name="depth"
                                            label={`${t("depth")} (${formatUnit("in", "cm")})`}
                                            variant="flat"
                                            size="sm"
                                            placeholder={t("depth")}
                                            className="flex-1"
                                            min={0}
                                            step={0.1}
                                            labelPlacement="outside"
                                            isDisabled={plotsEstablishment}
                                        />
                                    )}
                                </div>
                                <Divider className="my-2" />
                                <div className="md:flex md:flex-row md:gap-4 md:items-center w-full text-sm">
                                    <div className="flex flex-col gap-1">
                                        <label className="text-xs text-default-900">
                                            {t("status")}
                                        </label>
                                        <ButtonGroup>
                                            <Button
                                                onPress={() =>
                                                    form.setValue(
                                                        "is_complete",
                                                        false,
                                                    )
                                                }
                                                color={
                                                    isComplete
                                                        ? "default"
                                                        : "primary"
                                                }
                                                size="sm"
                                            >
                                                {t("pendingStatus")}
                                            </Button>
                                            <Button
                                                onPress={() =>
                                                    form.setValue(
                                                        "is_complete",
                                                        true,
                                                    )
                                                }
                                                color={
                                                    isComplete
                                                        ? "primary"
                                                        : "default"
                                                }
                                                size="sm"
                                            >
                                                {t("completeStatus")}
                                            </Button>
                                        </ButtonGroup>
                                    </div>
                                    <AutoCompleteInput
                                        options={methodOptions ?? []}
                                        label={t("method")}
                                        name="method"
                                        size="sm"
                                        labelPlacement="outside"
                                        placeholder={t("method")}
                                        className="flex-1"
                                        isClearable={false}
                                    />
                                </div>
                                <TextAreaInput
                                    className="flex-1"
                                    name="note"
                                    label={t("note")}
                                    variant="flat"
                                    size="sm"
                                    labelPlacement="outside"
                                    placeholder={t("note")}
                                />
                            </div>
                        </form>
                    </FormProvider>
                </ModalBody>
                <ModalFooter>
                    <Button
                        color="primary"
                        fullWidth
                        onPress={() => {
                            form.trigger();
                            form.handleSubmit(saveApplicationOperations)();
                        }}
                        isLoading={bulkLogApplicationMutation.isPending}
                    >
                        <FontAwesomeIcon
                            icon={faClipboardList}
                            className="mr-2"
                        />
                        Log Applications
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}

export default BulkLogApplication;
